import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SFService } from '../service/SF.service';
import { ContactService } from 'service/contact.service';

/**
 * Custom hook to handle file selection and generate a preview URL for a single image.
 * * @returns An object containing the necessary state, ref, and handlers.
 */
type UploadProfilePictureOptions = {
  key?: boolean;
  type?: 'sf' | 'contact';
};
export const useImagePreview = (initialUrl?: string | null) => {
  const [selectedFile, setSelectedFile] = useState<File | string | null>(initialUrl || null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const DEFAULT_UPLOAD_OPTIONS = {
    key: false,
    type: 'sf',
  };

  useEffect(() => {
    if (initialUrl !== previewUrl) {
      setSelectedFile(initialUrl || null);
      setPreviewUrl(initialUrl || null);
    }
  }, [initialUrl]);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      const file = fileList?.[0] || null;

      if (file) {
        setSelectedFile(file);
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else if (fileList && fileList.length === 0) {
        setSelectedFile(initialUrl || null);
        setPreviewUrl(initialUrl || null);
      } else {
        setSelectedFile(null);
        setPreviewUrl(null);
      }
    },
    [initialUrl],
  );

  const clearSelection = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const openFileSelection = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadProfilePicture = async (props?: UploadProfilePictureOptions) => {
    const options = { ...DEFAULT_UPLOAD_OPTIONS, ...props };

    if (selectedFile && selectedFile instanceof File) {
      const email = sessionStorage.getItem('current-user') || '';
      const getPresigned =
        options.type == 'contact'
          ? await ContactService.getPresigned(
              selectedFile.size.toString(),
              selectedFile.type,
              selectedFile.name,
            )
          : await SFService.getPresigned(email, selectedFile.type, selectedFile.name);

      await SFService.uploadFile(
        options.type == 'contact'
          ? { ...getPresigned.fields, file: selectedFile }
          : { ...getPresigned.fileds, file: selectedFile },
        getPresigned.fileds['Content-Type'],
      );

      if (options.key) {
        return getPresigned.fileds.key;
      }
      return `${getPresigned.url}${getPresigned.fileds.key}`;
    }
  };

  return {
    fileInputRef,
    selectedFile,
    previewUrl,
    handleFileChange,
    clearSelection,
    openFileSelection,
    uploadProfilePicture,
  };
};
